// src/pages/Products.js
// MODERNIZED: Nulek brand palette, charcoal tones, Barlow font
// PRESERVED: ALL fetch/API logic, fallback data, loading state, all state/hooks

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Products.module.css';

// ── ID-range → category mapping ──────────────────────────────────────────────
const CATEGORY_RANGES = [
  { key: 'planning',      label: 'Planning',      min:  1, max:  9 },
  { key: 'analysis',      label: 'Analysis',      min: 10, max: 19 },
  { key: 'finance',       label: 'Finance',       min: 20, max: 29 },
  { key: 'governance',    label: 'Governance',    min: 30, max: 39 },
  { key: 'collaboration', label: 'Collaboration', min: 40, max: 49 },
];

const getCategoryKey = (id) => {
  const match = CATEGORY_RANGES.find(r => id >= r.min && id <= r.max);
  return match ? match.key : 'other';
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/products/`)
      .then((res) => {
        if (!res.ok) throw new Error(`Error: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const fallbackData = [
          { id:  1, name: 'Schedule Optimization Suite',  description: 'Automate timeline analysis, detect delays, and optimize project schedules with AI-powered insights.',   link: '/products/schedule-suite',       icon: '📅', features: ['AI-powered scheduling', 'Delay prediction', 'Real-time adjustments', 'Team collaboration'] },
          { id:  2, name: 'Resource Planning Module',     description: 'Optimize team allocation, track capacity, and prevent burnout with smart workload analytics.',          link: '/products/resource-planner',     icon: '👥', features: ['Team allocation', 'Capacity tracking', 'Workload analytics', 'Burnout prevention'] },
          { id: 10, name: 'Risk Assessment Toolkit',      description: 'Identify, score, and mitigate project risks with dynamic dashboards and automated alerts.',             link: '/products/risk-toolkit',         icon: '⚠️', features: ['Risk scoring', 'Mitigation planning', 'Automated alerts', 'Dashboard views'] },
          { id: 20, name: 'Cost Control Dashboard',       description: 'Track budgets, forecast spend, and visualize cost trends in real time across all projects.',            link: '/products/cost-dashboard',       icon: '💰', features: ['Budget tracking', 'Cost forecasting', 'Visual analytics', 'Expense management'] },
          { id: 30, name: 'Compliance Audit Framework',   description: 'Ensure adherence to standards with automated checklists, audit trails, and reporting tools.',          link: '/products/compliance-framework', icon: '📋', features: ['Automated checklists', 'Audit trails', 'Compliance reporting', 'Regulatory updates'] },
          { id: 40, name: 'Stakeholder Portal',           description: 'Keep clients and executives informed with customizable reports and secure access dashboards.',         link: '/products/stakeholder-portal',   icon: '🌐', features: ['Custom reports', 'Secure dashboards', 'Real-time updates', 'Client portal'] },
        ];
        setProducts(data.length > 0 ? data : fallbackData);
        setLoading(false);
      })
      .catch((err) => {
        console.warn('Failed to fetch products, using fallback:', err);
        setProducts([
          { id:  1, name: 'Schedule Optimization Suite', description: 'AI-powered scheduling and delay detection.',      link: '/products/schedule-suite',       icon: '📅', features: ['AI-powered scheduling', 'Delay prediction', 'Real-time adjustments'] },
          { id:  2, name: 'Resource Planning Module',    description: 'Team capacity planning and workload analytics.',  link: '/products/resource-planner',     icon: '👥', features: ['Team allocation', 'Capacity tracking', 'Workload analytics'] },
          { id: 10, name: 'Risk Assessment Toolkit',     description: 'Dynamic risk scoring and mitigation planning.',   link: '/products/risk-toolkit',         icon: '⚠️', features: ['Risk scoring', 'Mitigation planning', 'Automated alerts'] },
          { id: 20, name: 'Cost Control Dashboard',      description: 'Real-time budget tracking and forecasting.',      link: '/products/cost-dashboard',       icon: '💰', features: ['Budget tracking', 'Cost forecasting', 'Visual analytics'] },
          { id: 30, name: 'Compliance Audit Framework',  description: 'Automated checklists and audit reporting.',       link: '/products/compliance-framework', icon: '📋', features: ['Automated checklists', 'Audit trails', 'Compliance reporting'] },
          { id: 40, name: 'Stakeholder Portal',          description: 'Customizable reports and secure dashboards.',     link: '/products/stakeholder-portal',   icon: '🌐', features: ['Custom reports', 'Secure dashboards', 'Real-time updates'] },
        ]);
        setLoading(false);
      });
  }, []);

  const categories = [
    { id: 'all', name: 'All Products' },
    ...CATEGORY_RANGES.map(r => ({ id: r.key, name: r.label })),
  ];

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => getCategoryKey(p.id) === activeCategory);

  const stats = [
    { value: '40%', label: 'Faster Planning'    },
    { value: '85%', label: 'Risk Reduction'     },
    { value: '30%', label: 'Cost Savings'       },
    { value: '95%', label: 'Client Satisfaction'},
  ];

  const testimonials = [
    { quote: "Nulek tools helped us cut project delays by 40% and improve stakeholder satisfaction across the board.", author: "Sarah Johnson", role: "Operations Director", company: "Global Engineering Firm", avatar: "👩‍💼" },
    { quote: "The AI-powered insights have transformed how we approach project planning. It's like having an expert consultant on the team 24/7.", author: "Michael Chen", role: "CTO", company: "Tech Innovations Inc.", avatar: "👨‍💻" },
    { quote: "Our compliance process went from a monthly headache to an automated breeze. The audit framework is incredible.", author: "Elena Rodriguez", role: "Compliance Officer", company: "Financial Services Group", avatar: "👩‍⚖️" },
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p className={styles.loadingText}>Loading products...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Our Product Suite | Nulek Consulting Solutions</title>
        <meta name="description" content="Explore Nulek Consulting's professional tools for schedule optimization, risk assessment, cost control, and stakeholder management." />
      </Helmet>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroAccentBar} />
        <div className={styles.heroBg} />
        <div className={styles.heroContent}>
          <p className={styles.eyebrow}>Product Suite</p>
          <h1 className={styles.heroTitle}>
            Powerful Tools for <span className={styles.heroAccent}>Project Excellence</span>
          </h1>
          <p className={styles.heroText}>
            Discover intelligent solutions designed to transform how you plan, manage, and deliver projects —
            faster, smarter, and more reliably than ever before.
          </p>
          <div className={styles.statsGrid}>
            {stats.map((stat, i) => (
              <div key={i} className={styles.statCard}>
                <div className={styles.statValue}>{stat.value}</div>
                <div className={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className={styles.benefitsSection}>
        <p className={styles.eyebrowCentered}>Why Nulek</p>
        <h2 className={styles.sectionTitle}>Why Choose Nulek?</h2>
        <div className={styles.benefitsGrid}>
          {[
            { icon: '🤖', title: 'AI-Driven Insights', text: 'Harness predictive analytics to stay ahead of risks and delays before they impact your projects.' },
            { icon: '🔒', title: 'Enterprise-Ready',   text: 'Built for security, compliance, and scale across global teams and complex organizations.' },
            { icon: '🚀', title: 'Proven Efficiency',  text: 'Trusted by teams worldwide to cut costs, boost delivery performance, and exceed expectations.' },
          ].map((b, i) => (
            <div key={i} className={styles.benefitCard}>
              <div className={styles.benefitIcon}>{b.icon}</div>
              <div className={styles.benefitAccent} />
              <h3 className={styles.benefitTitle}>{b.title}</h3>
              <p className={styles.benefitText}>{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <section className={styles.productsSection}>
        <div className={styles.productsHeader}>
          <p className={styles.eyebrowCentered}>Our Offering</p>
          <h2 className={styles.sectionTitle}>Our Product Suite</h2>
          <p className={styles.productsSubtext}>Comprehensive tools designed for every aspect of project management</p>
        </div>

        <div className={styles.categoryFilter}>
          {categories.map(cat => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                className={`${styles.categoryButton} ${isActive ? styles.categoryButtonActive : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.name}
              </button>
            );
          })}
        </div>

        <div className={styles.productsGrid}>
          {filteredProducts.length === 0 ? (
            <div className={styles.noResults}>
              <div className={styles.noResultsIcon}>📭</div>
              <p className={styles.noResultsText}>No products found in this category.</p>
            </div>
          ) : (
            filteredProducts.map((product, index) => (
              <div
                key={product.id}
                className={styles.productCard}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={styles.productHeader}>
                  <div className={styles.productIconWrapper}>{product.icon}</div>
                  <h3 className={styles.productName}>{product.name}</h3>
                </div>

                <div className={styles.productDivider} />

                <p className={styles.productDescription}>{product.description}</p>

                <ul className={styles.featuresList}>
                  {product.features && product.features.map((feature, i) => (
                    <li key={i} className={styles.featureItem}>
                      <span className={styles.featureCheck}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link to={product.link} className={styles.productButton}>
                  Explore Product
                </Link>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className={styles.testimonialsSection}>
        <p className={styles.eyebrowCentered}>Social Proof</p>
        <h2 className={styles.sectionTitleLight}>Trusted by Project Leaders Worldwide</h2>
        <div className={styles.testimonialsGrid}>
          {testimonials.map((t, i) => (
            <div key={i} className={styles.testimonialCard}>
              <div className={styles.testimonialAccent} />
              <div className={styles.testimonialAvatar}>{t.avatar}</div>
              <p className={styles.testimonialQuote}>"{t.quote}"</p>
              <div className={styles.testimonialAuthor}>
                <strong>{t.author}</strong>
                <span>{t.role}, {t.company}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaBg} />
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to Transform Your Projects?</h2>
          <p className={styles.ctaText}>
            Join thousands of teams using Nulek to deliver exceptional results.
            Start your free trial or request a personalized demo today.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/contact" className={styles.ctaPrimaryButton}>
              Request Demo
            </Link>
            <Link to="/contact" className={styles.ctaSecondaryButton}>
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;